/**
 * Template Slice
 *
 * Manages chat template state including:
 * - Active template type
 * - Template data
 * - Loading state for template transitions
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AssistantMessage, ActiveTemplate, TemplateData, TrackingInfo } from '@contracts';
import {
  DATA_ONLY_TEMPLATE_TYPES,
  STREAM_SKIP_TEMPLATE_TYPES,
} from '@/features/chat/constants/templateTypes';

// ============================================
// State Types
// ============================================

/**
 * Pending template type - indicates which template is being loaded
 * Used to show skeleton loaders during template transitions
 */
export type PendingTemplate =
  | 'flights-review-travellers'
  | 'flights-review-interstitial'
  | 'flights-thankyou'
  | 'hotels-thankyou'
  | null;

export interface TemplateState {
  activeTemplate: ActiveTemplate;
  templateData: TemplateData;
  /** Template that is currently being loaded (for skeleton display) */
  pendingTemplate: PendingTemplate;
  /** Tracking info from backend response (omniture, pdt, xdm) */
  trackingInfo: TrackingInfo | null;
}

// ============================================
// Initial State
// ============================================

export const initialTemplateState: TemplateState = {
  activeTemplate: null,
  templateData: null,
  pendingTemplate: null,
  trackingInfo: null,
};

// ============================================
// Slice
// ============================================

const templateSlice = createSlice({
  name: 'chat/template',
  initialState: initialTemplateState,
  reducers: {
    /**
     * Set pending template - shows skeleton loader for the target template
     * Call this before dispatching async thunks that will change templates
     */
    setPendingTemplate: (state, action: PayloadAction<PendingTemplate>) => {
      state.pendingTemplate = action.payload;
    },

    /**
     * Clear pending template - hides skeleton loader
     * Called automatically when setActiveTemplate is dispatched
     */
    clearPendingTemplate: (state) => {
      state.pendingTemplate = null;
    },

    /**
     * Set active template and data
     * Also clears pendingTemplate since the template has loaded
     */
    setActiveTemplate: (
      state,
      action: PayloadAction<{
        template: ActiveTemplate;
        data: TemplateData;
        trackingInfo?: TrackingInfo | null;
      }>,
    ) => {
      state.activeTemplate = action.payload.template;
      state.templateData = action.payload.data;
      state.trackingInfo = action.payload.trackingInfo ?? null;
      state.pendingTemplate = null;
    },

    /**
     * Update template from streaming message.
     *
     * - DATA_ONLY types (coupon-update, traveller-detail, etc.) are fully ignored here;
     *   they never become activeTemplate. Owning slices handle them via extraReducers.
     *
     * - STREAM_SKIP types (hotels-review-error) are skipped during live streaming
     *   so the user stays on the current page (owning slice shows a popup).
     *   They ARE renderable for chat history / card clicks via setActiveTemplate.
     */
    updateTemplateFromStream: (state, action: PayloadAction<AssistantMessage>) => {
      const streamedMessage = action.payload;

      const msgTemplate = streamedMessage.activeTemplate;
      if (msgTemplate && DATA_ONLY_TEMPLATE_TYPES.has(msgTemplate)) {
        return;
      }

      if (msgTemplate && STREAM_SKIP_TEMPLATE_TYPES.has(msgTemplate)) {
        return;
      }

      if (streamedMessage.activeTemplate) {
        state.activeTemplate = streamedMessage.activeTemplate as ActiveTemplate;
        state.pendingTemplate = null;
      }
      if (streamedMessage.templateData) {
        state.templateData = streamedMessage.templateData as TemplateData;
      }
      state.trackingInfo = streamedMessage.trackingInfo ?? null;
    },

    /**
     * Clear active template
     */
    clearActiveTemplate: (state) => {
      state.activeTemplate = null;
      state.templateData = null;
      state.pendingTemplate = null;
      state.trackingInfo = null;
    },

    /**
     * Reset template state
     */
    resetTemplateState: () => initialTemplateState,
  },
});

// ============================================
// Actions
// ============================================

export const {
  setPendingTemplate,
  clearPendingTemplate,
  setActiveTemplate,
  updateTemplateFromStream,
  clearActiveTemplate,
  resetTemplateState,
} = templateSlice.actions;

// ============================================
// Reducer Export
// ============================================

export const templateReducer = templateSlice.reducer;
